import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Box from '@mui/material/Box';
import MuiCard from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'src/common/components/Tooltip/Tooltip';
import { NetworkDetailsBlock } from './networkDetails.utils';

type NetworkDetailsProps = {
  homeNetwork?: NetworkDetailsBlock;
  lastRoamingNetwork?: NetworkDetailsBlock;
  testIdPrefix?: string;
};

const CardsContainer = styled(Box)(({ theme }) => ({
  alignItems: 'start',
  display: 'grid',
  gap: theme.spacing(16),
  gridAutoRows: 'min-content',
  gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
  width: '100%',
}));

const Card = styled(MuiCard)(({ theme }) => ({
  backgroundColor: '#F7F7F8',
  border: `1px solid ${theme.palette.border}`,
  borderRadius: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(12),
  padding: theme.spacing(20),
}));

const Label = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

const Value = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
}));

const SecondaryValue = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

const DetailValueStack = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

const LabelWithIcon = styled(Box)(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  gap: theme.spacing(4),
}));

const CountryFlag = styled('span')({
  alignItems: 'center',
  display: 'inline-flex',
  height: 15,
  minWidth: 15,
});

const CountryFlagImage = styled('img')({
  display: 'block',
  height: 10,
  width: 15,
});

const hasValue = (value?: string) => !!value && value.trim().length > 0;
const isoCodePattern = /^[A-Z]{2}$/;

const getCountryFlagEmoji = (isoCode?: string) => {
  if (isoCode === undefined || !hasValue(isoCode)) {
    return undefined;
  }

  const normalizedIsoCode = isoCode.trim().toUpperCase();

  if (!isoCodePattern.test(normalizedIsoCode)) {
    return undefined;
  }

  return `https://flagcdn.com/${normalizedIsoCode.toLowerCase()}.svg`;
};

const getRatTooltipTitle = (value?: string, secondaryValue?: string) => {
  if (hasValue(value) && hasValue(secondaryValue)) {
    return `${value} (${secondaryValue})`;
  }

  if (hasValue(value)) {
    return value;
  }

  if (hasValue(secondaryValue)) {
    return secondaryValue;
  }

  return undefined;
};

const hasAnyValue = (block?: NetworkDetailsBlock) => {
  if (!block) {
    return false;
  }

  return [
    block.networkName,
    block.networkCode,
    block.countryName,
    block.isoCode,
    block.region,
    block.mnc,
    block.mcc,
    block.plmn,
    block.lastRatFriendlyName,
    block.lastRatCode,
    block.lastRatTooltip,
  ].some(hasValue);
};

const DetailValue = ({ value, secondaryValue }: { value?: string; secondaryValue?: string }) => {
  if (!hasValue(value) && !hasValue(secondaryValue)) {
    return <EmptyDetailValue />;
  }

  return (
    <DetailValueStack>
      {hasValue(value) ? <Value variant="text1">{value}</Value> : null}
      {hasValue(secondaryValue) ? <SecondaryValue variant="text3">{secondaryValue}</SecondaryValue> : null}
    </DetailValueStack>
  );
};

const EmptyDetailValue = () => (
  <DetailValueStack>
    <Value variant="text1">-</Value>
  </DetailValueStack>
);

const CountryDetailValue = ({ countryName, isoCode }: { countryName?: string; isoCode?: string }) => {
  const countryFlagUrl = getCountryFlagEmoji(isoCode);

  if (!hasValue(countryName) && !hasValue(isoCode)) {
    return <EmptyDetailValue />;
  }

  return (
    <DetailValueStack>
      {hasValue(countryName) ? (
        <LabelWithIcon>
          {countryFlagUrl ? (
            <CountryFlag aria-hidden="true">
              <CountryFlagImage alt="" src={countryFlagUrl} loading="lazy" />
            </CountryFlag>
          ) : null}
          <Value variant="text1">{countryName}</Value>
        </LabelWithIcon>
      ) : null}
      {hasValue(isoCode) ? <SecondaryValue variant="text3">{isoCode}</SecondaryValue> : null}
    </DetailValueStack>
  );
};

const RatLabel = ({
  label,
  tooltipTitle,
  tooltipDescription,
}: {
  label: string;
  tooltipTitle?: string;
  tooltipDescription?: string;
}) => {
  if (!hasValue(tooltipDescription)) {
    return <Label variant="text2">{label}</Label>;
  }

  return (
    <LabelWithIcon>
      <Label variant="text2">{label}</Label>
      <Tooltip
        title={tooltipTitle ?? ''}
        description={tooltipDescription}
        variant="dark"
        showArrow
        placement="top"
        enterDelay={300}
      >
        <InfoOutlinedIcon sx={{ color: 'text.secondary', fontSize: 14 }} />
      </Tooltip>
    </LabelWithIcon>
  );
};

const NetworkCard = ({
  title,
  block,
  testIdPrefix,
  showRat,
}: {
  title: string;
  block: NetworkDetailsBlock;
  testIdPrefix: string;
  showRat: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <Card data-testid={`${testIdPrefix}.card`}>
      <SecondaryValue variant="text3">{title}</SecondaryValue>

      <Grid container spacing={12}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Label variant="text2">{t('simInventory.network')}</Label>
          <DetailValue value={block.networkName} secondaryValue={block.networkCode} />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Label variant="text2">{t('simInventory.countryIso')}</Label>
          <CountryDetailValue countryName={block.countryName} isoCode={block.isoCode} />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          {showRat ? (
            <>
              <RatLabel
                label={t('simInventory.lastRat')}
                tooltipTitle={getRatTooltipTitle(block.lastRatFriendlyName, block.lastRatCode)}
                tooltipDescription={block.lastRatTooltip}
              />
              <DetailValue value={block.lastRatFriendlyName} secondaryValue={block.lastRatCode} />
            </>
          ) : (
            <>
              <Label variant="text2">{t('simInventory.region')}</Label>
              <DetailValue value={block.region} />
            </>
          )}
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Label variant="text2">{t('simInventory.mnc')}</Label>
          <DetailValue value={block.mnc} />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Label variant="text2">{t('simInventory.mcc')}</Label>
          <DetailValue value={block.mcc} />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Label variant="text2">{t('simInventory.plmn')}</Label>
          <DetailValue value={block.plmn} />
        </Grid>
      </Grid>
    </Card>
  );
};

export const NetworkDetails = ({
  homeNetwork,
  lastRoamingNetwork,
  testIdPrefix = 'networkIdentifiers',
}: NetworkDetailsProps) => {
  const { t } = useTranslation();

  if (!hasAnyValue(homeNetwork) && !hasAnyValue(lastRoamingNetwork)) {
    return null;
  }

  return (
    <Box data-testid={testIdPrefix}>
      <CardsContainer>
        {homeNetwork ? (
          <NetworkCard
            title={t('simInventory.homeNetwork')}
            block={homeNetwork}
            testIdPrefix={`${testIdPrefix}.homeNetwork`}
            showRat={false}
          />
        ) : null}
        {lastRoamingNetwork ? (
          <NetworkCard
            title={t('simInventory.lastRoamingNetwork')}
            block={lastRoamingNetwork}
            testIdPrefix={`${testIdPrefix}.lastRoamingNetwork`}
            showRat={true}
          />
        ) : null}
      </CardsContainer>
    </Box>
  );
};